import {
    afterNextRender, AfterContentInit, ContentChildren, DestroyRef, Directive, inject, Injector,
    OnDestroy, OnInit, QueryList
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { merge } from "rxjs";
import { MatTable } from "@angular/material/table";
import { LIST_STATE_SERVICE } from "../constants/tokens";
import { IListStateService } from "../interfaces/list-state-service";
import { ListDataSourceService } from "../services/list-data-source.service";
import { RowDirective } from "./row.directive";

/**
 * TableDirective (`mgdTable`)
 *
 * A structural attribute directive applied to `mat-table` (or a plain `<table>`)
 * that wires up the `ListDataSourceService` and scroll-to-row behaviour provided
 * by `IListStateService`.
 *
 * **Data source binding** — In `ngOnInit` the directive assigns the injected
 * `ListDataSourceService` to `MatTable.dataSource`, removing the need for
 * `[dataSource]="dataSource"` in every list template.
 *
 * **Scroll-to-row** — Whenever a scroll target is pending (`scrollToRowId`) the
 * directive resolves it against the loaded rows: a row the list does not contain
 * is reported via `rowNotRendered()`, and a row it does contain is scrolled into
 * view with `scrollIntoView()` and reported via `rowRendered()`.
 *
 * Whether the list contains the row is decided from the state service's row
 * positions, not from the `RowDirective` query: `CdkTable.contentChanged` fires
 * from inside `renderRows()`, before Angular has updated `@ContentChildren` for
 * the row views it just created, so the query is still empty on that pass and a
 * present row would otherwise be reported as missing. The lookup used for the
 * scroll itself runs from `afterNextRender`, once the row views exist and their
 * `row` inputs are bound.
 *
 * Content changes seen before the data source has loaded are ignored entirely —
 * the table renders once against the data source's seeded empty array while the
 * first request is in flight, and that render says nothing about what the list
 * contains.
 *
 * **Row state lifetime** — Row states live on the state service, which outlives
 * the table (it is shared with the rest of the feature group). On destroy the
 * directive clears them so a highlight or error raised for one visit to the list
 * is not still showing the next time the list is opened.
 *
 * @example
 * ```html
 * <mat-table mgdTable>
 *   <!-- column / row definitions -->
 * </mat-table>
 * ```
 */
@Directive({
    selector: "mat-table[mgdTable], table[mgdTable]"
})
export class TableDirective<M extends object, O extends object, R extends object>
implements OnInit, AfterContentInit, OnDestroy {

    /** All `RowDirective` instances within this table's content. */
    @ContentChildren(RowDirective) rows!: QueryList<RowDirective>;

    private readonly _table = inject(MatTable, { self: true });
    private readonly _dataSource = inject(ListDataSourceService) as ListDataSourceService<M, O, R>;
    private readonly _state = inject(LIST_STATE_SERVICE) as IListStateService<M, O, R>;
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _injector = inject(Injector);

    /** @inheritdoc */
    ngOnInit(): void {
        this._table.dataSource = this._dataSource;
    }

    /**
     * Angular lifecycle hook that starts resolving pending scroll targets.
     *
     * Both signals are needed: `contentChanged` reports a load that rendered no rows,
     * where the `rows` query never changes, and the `rows` query update reports the row
     * views created by that load, which `contentChanged` fires too early to see. Each
     * one resolves from `afterNextRender` rather than inline, because neither fires late
     * enough for the row views to have their `row` inputs bound.
     *
     * `rows` only exists once content children have been resolved, which is why this
     * runs here rather than in `ngOnInit`.
     */
    ngAfterContentInit(): void {
        const rendered$ = merge(this._table.contentChanged, this.rows.changes);

        rendered$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => this._scheduleScrollToRow());
    }

    /**
     * Angular lifecycle hook that clears the row states this table rendered, so they do
     * not persist on the shared state service after the table is gone.
     */
    ngOnDestroy(): void {
        this._state.clearRowStates();
    }

    /**
     * Queues a scroll-target resolution for after the next render.
     *
     * Neither trigger fires late enough to read the row views: `contentChanged` runs
     * inside `renderRows()` and the `rows` query update runs before the row inputs are
     * bound, so the lookup has to wait for the render to finish.
     */
    private _scheduleScrollToRow(): void {
        afterNextRender(() => this._handleScrollToRow(), { injector: this._injector });
    }

    /**
     * Resolves a pending scroll target against the rows the data source has loaded.
     *
     * Does nothing until the data source reports a completed load, so the empty render
     * performed while the first request is in flight cannot be read as a miss.
     *
     * A row the loaded list does not contain is reported once via `rowNotRendered` and the
     * pending id is cleared. A row it does contain but whose `RowDirective` is not usable
     * yet leaves the pending id in place, so a later signal performs the scroll instead of
     * the row being reported as missing.
     */
    private _handleScrollToRow(): void {
        if (!this._dataSource.loaded) {
            return;
        }
        const id = this._state.scrollToRowId;
        if (!id) {
            return;
        }
        if (this._state.getRowPosition(id) === undefined) {
            this._state.clearScrollToRow();
            this._state.rowNotRendered(id);
            return;
        }
        // The `rows` query can surface a RowDirective on the same pass its view is created,
        // before the `row` input is bound; those candidates cannot be matched yet.
        const row = this.rows.find(
            (candidate) => !!candidate.row && this._state.matchesRow(candidate.row as M, id)
        );
        if (!row) {
            return;
        }
        this._state.clearScrollToRow();
        row.scrollIntoView();
        this._state.rowRendered(id);
    }
}
